import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { TaskSidePanel } from 'app/components/Tasks/CustomComponents/TaskSidePanel/TaskSidePanel';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import { TaskSidebarContainer } from 'app/components/Tasks/TaskSidebar/TaskSidebarContainer';
import { convertToFormat } from 'app/helpers/dateHelper';
import { updateLastVerifiedData } from 'app/models/Orders/OrderDetails';
import { ITaskStickChosen, TaskEntity } from 'app/models/Tasks/Tasks';
import { updateLastVerified } from 'app/services/OrderServices/OrderServices';
import { selectUser } from 'oidc/user.selectors';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrderDetailsUIView } from '../Store/Order.selectors';
import { CreatePlacementBtn } from './CreatePlacementBtn';
import { Authorized } from 'oidc/userHelper';
import { useReadOnly, userRoles } from 'oidc/userRoles';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { useDecision } from '@optimizely/react-sdk';
import { ff_order_ui_inheritfromunitbutton } from 'app/constants/FeatureFlags/Order/Keys';

const useStyles = makeStyles<{ taskShrink: boolean }>()((theme, props) => ({
  verifyButton: {
    fontSize: 12,
    marginLeft: '10px',
  },
  sidePanel: {
    width: props.taskShrink ? 36 : '297px',
    '& .MuiPaper-root.MuiPaper-elevation1': {
      top: '60px',
      height: '90%',
      zIndex: 99,
    },
  },
  unitButton: {
    fontSize: 12,
    marginRight: '10px',
  },
}));

export const OrderHeaderWrapper = ({
  stickIconArray,
  taskStickChosen,
  setOpenCreatePlacementForm,
  setLastVerifiedUpdateStatus,
  id,
  editStarted,
}: {
  stickIconArray: number[];
  taskStickChosen?: ITaskStickChosen;
  setOpenCreatePlacementForm: (e) => void;
  setLastVerifiedUpdateStatus: (e) => void;
  id: any;
  editStarted: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector(selectUser);
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const { readOnly } = useReadOnly();
  const [acceptUnitRequirementsModal, setAcceptUnitRequirementsModal] = useState<boolean>(false);
  const [showInheritUnitButton] = useDecision(ff_order_ui_inheritfromunitbutton);
  const handleAcceptUnitRequirements = () => {
    setAcceptUnitRequirementsModal(true);
  };
  const handleAcceptUnitRequirementsDialogClose = () => {
    setAcceptUnitRequirementsModal(false);
  };
  const handleAcceptUnitRequirementsConfirm = () => {
    dispatch(orderCreationNewActions.onEditOrderInheritUnit());
    setAcceptUnitRequirementsModal(false);
  };

  const userInfo = {
    userId: userData?.userInfo?.employeeId,
    userName: userData?.userInfo?.firstName + ' ' + userData?.userInfo?.lastName,
  };

  const taskSidePanelItems = [
    {
      title: t('notification.taskSidePanel.taskSidePanelHeading'),
      tooltip: t('notification.taskSidePanel.taskSidePanelHeading'),
      component: <TaskSidebarContainer container={TaskEntity.ORDERS} />,
    },
  ];
  const { classes } = useStyles({ taskShrink: taskStickChosen.stick === null });

  const handleTaskStickChoice = stick => {
    dispatch(
      taskDetailsActions.setTaskStickChosen({
        entity: TaskEntity.ORDERS,
        stick: stick === taskStickChosen.stick ? null : stick,
      }),
    );
  };

  const handleLastVerified = async () => {
    const verifiedDateTime = convertToFormat(new Date(), '');
    const lastVerifiedData: updateLastVerifiedData = {
      orderId: parseInt(id),
      DateVerified: verifiedDateTime ? verifiedDateTime : '',
      VerifiedByUserId: userInfo.userId ? userInfo.userId : 0,
    };
    const response = await updateLastVerified(lastVerifiedData);
    if (response) {
      setLastVerifiedUpdateStatus(false);
      dispatch(
        globalActions.setSnackBar({
          message: t(`The order ${id} is verified successfully`),
          severity: 'success',
        }),
      );
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: t(`The order ${id} is verified failed`),
          severity: 'error',
        }),
      );
    }
    setLastVerifiedUpdateStatus(true);
  };

  return (
    <React.Fragment>
      <Grid container spacing={2} wrap="nowrap" alignItems={'center'}>
        <Grid item>
          <Grid container alignItems="center" style={{ display: 'flex' }}>
            <Grid item>
              {showInheritUnitButton?.enabled && selectOrderUiDetails?.orderDetails?.showInheritUnitButton && (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  classes={{ containedPrimary: `${classes.unitButton}` }}
                  onClick={handleAcceptUnitRequirements}
                  children={t('order.newOrder.acceptUnitRequirements')}
                />
              )}
            </Grid>
            <Grid item>
              <CreatePlacementBtn
                editStarted={editStarted}
                division={selectOrderUiDetails?.orderDetails?.division}
                orderType={selectOrderUiDetails?.orderDetails?.orderType}
                maxFilesSent={selectOrderUiDetails?.orderDetails?.maxFilesSent}
                positionsAvailable={selectOrderUiDetails?.quickGlanceDetails?.positionsAvailable}
                onClick={() => setOpenCreatePlacementForm(true)}
                disabled={ readOnly }
              />
            </Grid>
            <Grid item>
              <CustomTooltip
                disabled={Authorized(
                  [
                    userRoles.accountManagement_Leadership,
                    userRoles.accountManagement_TeamMember,
                    userRoles.supportAdmin,
                  ],
                  userData.userInfo,
                )}
                standardMargin
                isStandardContent
                tooltipContent={t('global.accessDisabled')}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  classes={{ containedPrimary: `${classes.verifyButton}` }}
                  onClick={handleLastVerified}
                  children={t('search.globalSearch.order.verifyOrder')}
                  disabled={
                    !Authorized(
                      [
                        userRoles.accountManagement_Leadership,
                        userRoles.accountManagement_TeamMember,
                        userRoles.supportAdmin,
                      ],
                      userData.userInfo,
                    )
                  }
                />
              </CustomTooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <div className={classes.sidePanel}>
            {stickIconArray && taskSidePanelItems && (
              <TaskSidePanel
                items={taskSidePanelItems}
                stickChosen={taskStickChosen}
                handleChoice={handleTaskStickChoice}
                iconArray={stickIconArray}
                scrollable={true}
              />
            )}
          </div>
        </Grid>
      </Grid>
      <GenericDialog
        open={acceptUnitRequirementsModal}
        dialogTitleProps={{
          text: t('order.newOrder.areYouSure'),
          style: { paddingBottom: '12px' },
        }}
        dialogContentProps={{
          style: { fontSize: '14px', paddingBottom: '24px' },
        }}
        fullWidth
        maxWidth={'sm'}
        disableEscapeKeyDown
        dialogActions={[
          {
            onClick: handleAcceptUnitRequirementsDialogClose,
            text: t('order.facility.creditHoldDialog.cancelBtn'),
            variant: 'contained',
            color: 'tertiary',
          },
          {
            onClick: handleAcceptUnitRequirementsConfirm,
            text: t('order.newOrder.confirm'),
            variant: 'contained',
          },
        ]}
      >
        <Typography>{t('order.newOrder.overWriteDataMsg')}</Typography>
      </GenericDialog>
    </React.Fragment>
  );
};
